import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useRecoilState, useRecoilValue } from 'recoil';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import TableHeader from '../../Shared/TableHeader/index.jsx';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../Shared/BotNumberDropdown/index.tsx';
import ReactPagination from '../../Shared/Pagination/index.jsx';
import { ECommerceActions } from '../../../Services/_actions/ECommerce/ecommerce.action.js';
import { GetStatusBadges } from '../../InterveneDashboard/Constant/GetStatusBadges.jsx';
import {
  channelListAtom,
  productFiltersAtom,
  productListObjAtom,
  selectedProductAtom,
  userInformationAtom,
} from '../../../Services/_states/index.js';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { PaginatedResponse } from '../../../interfaces/Management.interface.ts';
import ProductDetails from './ProductDetails.tsx';
import useDebounce from '../../Shared/Delay/index.tsx';

const AvailabilityValues = [
  { label: 'In stock', value: 'in stock' },
  { label: 'Out of stock', value: 'out of stock' },
  { label: 'Pre-order', value: 'preorder' },
  { label: 'Discontinued', value: 'discontinued' },
];

const ProductList: React.FC = () => {
  const actions = ECommerceActions();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [productDataObj, setProductDataObj] = useRecoilState<PaginatedResponse>(productListObjAtom);
  const [selectedProduct, setSelectedProduct] = useRecoilState<any>(selectedProductAtom);
  const [filterObj, setFilterObj] = useRecoilState<any>(productFiltersAtom);
  const [refreshFilters, setRefreshFilters] = useState<any>({
    bot_number: '',
    catalog_id: '',
    organisation: '',
    organisationId: '',
  });
  const [catalogList, setCatalogList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshIconLoading, setRefreshIconLoading] = useState(false);
  const [showDetailComp, setShowDetailComp] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState(filterObj?.search || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { showSnackBar } = useSnackBar();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConfirm = async () => {
    if (confirmationAction && selectedProduct) {
      const { availability } = confirmationAction;
      const botNumber = selectedProduct?.bot_number;
      const productId = selectedProduct?.p_id;
      try {
        const res: any = await actions.updateProductItem({
          bot_number: botNumber,
          p_id: productId,
          availability,
        });
        if (res?.status === 200) {
          let updatedProductList = productDataObj?.list?.map((product) =>
            product?.p_id === productId
              ? {
                  ...product,
                  availability: availability,
                }
              : product
          );
          setProductDataObj({ ...productDataObj, list: updatedProductList });
          showSnackBar(`Availability updated to "${availability}" successfully.`, 'success');
          return res?.data;
        } else {
          showSnackBar('Failed to update availability', 'error');
        }
      } catch (error) {
        showSnackBar('Failed to update availability.', 'error');
      } finally {
        setOpenConfirmDialog(false);
        setSelectedProduct(null);
      }
    }
  };

  const handleCancel = () => {
    setOpenConfirmDialog(false);
  };

  const onClickDropdown = (e: React.MouseEvent, value: string) => {
    e.preventDefault();
    setConfirmationAction({ availability: value });
    setOpenConfirmDialog(true);
    handleMenuClose();
  };

  useEffect(() => {
    if (refreshFilters.bot_number && refreshFilters.catalog_id) {
      getProductItemList({
        ...filterObj,
        bot_number: refreshFilters.bot_number,
        catalog_id: refreshFilters.catalog_id,
      });
    }
  }, [refreshFilters]);

  const getProductItemList = async (data: any) => {
    setIsLoading(true);
    await actions.getProductItemList(data);
    setIsLoading(false);
  };

  const getCatalogList = async (botNumber: string) => {
    let res = await actions.getCatalogList(botNumber);
    setCatalogList(res?.data || []);
  };

  const postProductSetsList = async () => {
    await actions.postProductSetsList(refreshFilters);
  };

  const refreshProductList = async () => {
    if (refreshFilters?.bot_number && refreshFilters?.catalog_id) {
      setRefreshIconLoading(true);
      postProductSetsList();
      const filterUpdateObj = {
        ...filterObj,
        bot_number: refreshFilters.bot_number,
        catalog_id: refreshFilters.catalog_id,
      };
      await actions.refreshProductList(refreshFilters);
      setRefreshIconLoading(false);
      setFilterObj({ ...filterUpdateObj, ...refreshFilters });
      getProductItemList({ ...filterUpdateObj, ...refreshFilters });
    } else {
      showSnackBar('Please select channel and catalog', 'error');
    }
  };

  const handleRowClick = (row: any) => {
    setSelectedProduct(row);
    setShowDetailComp('Edit');
  };
  const onSearch = (value) => {
    setSearchTerm(value);
  };

  useEffect(() => {
    setFilterObj({ ...filterObj, search: debouncedSearchTerm });

    if (debouncedSearchTerm?.length > 2 || !debouncedSearchTerm) {
      getProductItemList({ ...filterObj, search: debouncedSearchTerm });
    }
  }, [debouncedSearchTerm]);

  const handleSelectBot = (e) => {
    setCatalogList([]);
    setRefreshFilters({
      ...refreshFilters,
      bot_number: e?.wa_phone_number,
      channelObj: e,
      catalog_id: '',
    });
    setFilterObj({
      ...filterObj,
      bot_number: e?.wa_phone_number || '',
      channelObj: e,
      catalog_id: '',
    });
    getProductItemList({ ...filterObj, bot_number: e?.wa_phone_number || '', channelObj: e });
    if (e && e?.wa_phone_number) getCatalogList(e?.wa_phone_number);
  };

  const handleSelectOrganisation = (e) => {
    setRefreshFilters({
      ...refreshFilters,
      organisation: e,
      organisationId: e?.organisation_id || '',
      bot_number: '',
      channelObj: null,
      catalog_id: '',
    });
    setFilterObj({
      ...filterObj,
      organisation: e,
      organisationId: e?.organisation_id || '',
      bot_number: '',
      channelObj: null,
      catalog_id: '',
    });
    // getProductItemList({ ...filterObj, organisation: e, organisationId: e?.organisation_id || '', bot_number: "", channelObj: null,catalog_id:"" });
  };

  const columns = [
    // {
    //   accessorKey: 'select',
    //   header: <input type="checkbox" />,
    //   Cell: ({ row }: any) => <input type="checkbox" />,
    // },
    {
      accessorKey: 'name',
      header: 'Product Name',
      Cell: ({ row }: any) => (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
          onClick={() => handleRowClick(row.original)}
        >
          <img src={row.original.image_url} style={{ width: 30, height: 30 }} />
          {row.original.name}
        </Box>
      ),
    },
    { accessorKey: 'brand', header: 'Brand' },
    {
      accessorKey: 'product_sets',
      header: 'Sets',
      Cell: ({ row }: any) =>
        row.original?.product_sets?.map((item) => item?.name).join(',') || 'N/A',
    },
    { accessorKey: 'quantity_to_sell_on_facebook', header: 'Quantity' },
    { accessorKey: 'price', header: 'Price' },
    { accessorKey: 'sale_price', header: 'Sale Price' },
    {
      accessorKey: 'availability',
      header: 'Availability',
      Cell: ({ row }: any) => (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {GetStatusBadges(row.original.availability)}
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.original)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        padding: '5px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      {showDetailComp ? (
        <ProductDetails mode={showDetailComp} backToList={setShowDetailComp} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ alignItems: 'center', mb: 1, mt: 0 }}>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                Products
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={() => {
                  setShowDetailComp('Add');
                  setSelectedProduct(null);
                }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              ml: 'auto',
              pb: 1,
              my: 1,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            {loggedInUser?.is_superuser && (
              <OrganisationDropdown
                sx={{ minWidth: 165 }}
                label={'Select Organisation'}
                value={filterObj?.organisation || null}
                onChange={handleSelectOrganisation}
              />
            )}
            <BotNumberDropdown
              sx={{ minWidth: 160 }}
              organisation={
                (loggedInUser?.is_superuser
                  ? filterObj?.organisation
                  : loggedInUser?.profile?.organisation) || null
              }
              onChange={handleSelectBot}
              value={filterObj?.channelObj}
              label="Select Bot Number"
            />
            {catalogList?.length > 0 && (
              <SearchableDropdown
                label={'Select Catalog'}
                options={catalogList}
                value={catalogList?.find((item) => item?.id === refreshFilters?.catalog_id) || null}
                labelKey="name"
                valueKey="id"
                onChange={(e) => {
                  setRefreshFilters({ ...refreshFilters, catalog_id: e?.id });
                  setFilterObj({ ...filterObj, catalog_id: e?.id || '' });
                  getProductItemList({ ...filterObj, catalog_id: e?.id || '' });
                }}
              />
            )}
            {refreshFilters?.bot_number && refreshFilters?.catalog_id && (
              <IconButton onClick={refreshProductList} color="primary" sx={{ p: 1 }}>
                {refreshIconLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            )}
            <SearchBox handleSearch={onSearch} value={searchTerm} />
          </Box>
          <CommonTable
            columns={columns}
            data={productDataObj?.list || []}
            isLoading={isLoading}
            muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 250px)', width: '100%' } }}
            renderBottomToolbar={() => (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
                <Pagination
                  totalCount={productDataObj?.count || 0}
                  rowsPerPage={filterObj?.rowsPerPage || 10}
                  page={filterObj?.page || 0}
                  handleChangePage={(e, newPage) => {
                    getProductItemList({ ...filterObj, page: newPage });
                    setFilterObj({ ...filterObj, page: newPage });
                  }}
                  handleChangeRowsPerPage={(e) => {
                    setFilterObj({
                      ...filterObj,
                      rowsPerPage: parseInt(e.target.value, 10),
                      page: 0,
                    });
                    getProductItemList({
                      ...filterObj,
                      rowsPerPage: parseInt(e.target.value, 10),
                      page: 0,
                    });
                  }}
                />
              </Box>
            )}
          />
        </>
      )}

      <ConfirmBox
        open={openConfirmDialog}
        onClose={(confirmed) => {
          if (confirmed) {
            handleConfirm();
          } else {
            handleCancel();
          }
        }}
        options={{
          title: 'Confirm Availability Change',
          text: `Are you sure you want to change the availability of ${selectedProduct?.name}?`,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {AvailabilityValues.map((item) => (
          <MenuItem key={item.value} onClick={(e) => onClickDropdown(e, item.value)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ProductList;
